import { PlaylistContentDownloadDto } from "collaboration-service";
import { Button, LoaderInline, Theme, useThemePart } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { useSelector } from 'react-redux';
import { ApplicationState } from "services/ApplicationState/ApplicationState";
import { shallowCompare } from "services/Helpers";
import { useImgI18N } from "services/ImgI18N";
import { PlaylistEditState, useSelectorPLE } from "./PlaylistEdit";
import { ImplementedElementsHeadlineOnlyAAO, SequenceContainerOuter } from "./PlaylistEditClasses";
import { getPlaylistContentData } from "./PlaylistEditContentImplementedElement";
import PlaylistEditSequenceImplementedElement from "./PlaylistEditSequenceImplementedElement";
import { PlaylistEditSequenceLinearSC as T } from "./PlaylistEditSequenceLinearSC";
import { usePLEState } from "./usePLEState";


// const valuesPlaylistEdgeTransitionFunctionReduced = _.filter(valuesPlaylistEdgeTransitionFunction, value =>
//     value === "FallThru" || value === "PercentageDone"
// );

const mapper = (state: ApplicationState) => ({
    missing: state.playlistsState.missing,
    contentLanguage: state.contentLanguage,
});

interface SchemeColor {
    value: string;
    label: string;
}

export const playlistEditNodeSchemeColors: SchemeColor[] = [
    { value: "@darkGrey", label: "Dark Grey" },
    { value: "@accentRed", label: "Red" },
    { value: "@accentOrange", label: "Orange" },
    { value: "@accentYellow", label: "Yellow" },
    { value: "@accentGreen", label: "Green" },
    { value: "@accentTurquoise", label: "Turquoise" },
    { value: "@accentBlue", label: "Blue" },
    { value: "@accentLightBlue", label: "Light Blue" },
    { value: "@accentPurple", label: "Purple" },
    { value: "@accentPink", label: "Pink" },
];

interface PlaylistEditTrainerContentProps {
}

const pleMapper = (state: PlaylistEditState) => ({
    posts: state.posts,
    playlist: state.playlist,
    expertMode: state.expertMode,
    contents: state.playlist?.contents ?? [],
});

const PlaylistEditTrainerContent = (p: PlaylistEditTrainerContentProps) => {
    const { posts, contents: playlistContents } = useSelectorPLE(pleMapper, shallowCompare);
    const s_trainerDocumentsReferenceIds = usePLEState(s => s.trainerDocumentsReferenceIds);
    const { contentLanguage } = useSelector(mapper, shallowCompare);
    const { t, currentLanguage } = useImgI18N("playlist");
    // const [expertMode, setExpertMode] = React.useState<boolean>(false);
    const [rightWidth, setRightWidth] = React.useState<number>(0);

    const cHeight = useThemePart((t: Theme) => t.content.height);
    const maxLeftHeight = cHeight - 159;
    const maxRightHeight = cHeight - 200;
    let selectedNodeColor: string | undefined = undefined;

    const lng = contentLanguage ?? currentLanguage

    const leftContainerRef = React.useRef<HTMLDivElement | null>(null);
    const rightContainerRef = React.useRef<HTMLDivElement | null>(null);

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const contents = React.useMemo(() => {
        // for now only from CMS!!
        const cts = _.map(_.filter(playlistContents, x => x.referenceType === "FileEntry"), pc => {
            const post = _.find(posts, s => s.id === pc.referenceId)!;
            if (post)
                return { contents: pc, post, title: getPlaylistContentData(pc, lng, post).title };
            return undefined;
        });
        return _.compact(cts);
    }, [playlistContents, posts, lng]);

    React.useEffect(() => {
        if (!containerRef.current) return;
        const updateWidth = () => {
            if (containerRef.current) {
            }
        };
        const observer = new ResizeObserver(updateWidth);
        observer.observe(containerRef.current);
        window.addEventListener("resize", updateWidth);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateWidth);
        };
    }, [rightWidth]);

    const updateWidths = () => {
        if (leftContainerRef.current && rightContainerRef.current) {
            const rightWidthValue = rightContainerRef.current.offsetWidth - parseInt(window.getComputedStyle(rightContainerRef.current).paddingLeft, 10) - parseInt(window.getComputedStyle(rightContainerRef.current).paddingRight, 10) - parseInt(window.getComputedStyle(rightContainerRef.current).borderLeftWidth, 10) - parseInt(window.getComputedStyle(rightContainerRef.current).borderRightWidth, 10);

            setRightWidth(rightWidthValue);
        }
    };

    React.useEffect(() => {
        updateWidths();
        window.addEventListener('resize', updateWidths);
        return () => {
            window.removeEventListener('resize', updateWidths);
        };

    }, []);

    const sortedContents = _.sortBy(contents, c => c.contents.referenceType);
    const groupedContents = _.groupBy(sortedContents, c => c.contents.referenceType);
    const minNodeWith = 10;
    const isLarge = rightWidth > minNodeWith;
    const showExpView = (isLarge);

    const getPostHeadlineForContent = React.useMemo(() => (content: PlaylistContentDownloadDto): string => {
        // console.log("PlaylistEditSequenceLinear.tsx::461 => posts", posts);

        const pc = _.find(posts, (c) => c.id === content.referenceId);
        // console.log("PlaylistEditSequenceLinear.tsx::464 => pc", pc);

        return getPlaylistContentData(content, lng, pc)?.title ?? "";
    }, [posts, lng]);

    const selectAllElements = () => {
        const allReferenceIds: string[] = _.flatten(
            _.map(groupedContents, group =>
                group.map(content => content.contents.referenceId).filter((id): id is string => !!id)
            )
        );
        s_trainerDocumentsReferenceIds.setValue(_.uniq([...(s_trainerDocumentsReferenceIds.value ?? []), ...allReferenceIds]));
    };

    const deSelectAllElements = () => {
        s_trainerDocumentsReferenceIds.setValue([]);
    };

    // console.log("render", { s_trainerDocumentsReferenceIds: s_trainerDocumentsReferenceIds.value, groupedContents });

    return (
        <div>
            <T.SeqContainerOuter minWidth={minNodeWith} style={{ borderTop: "none" }}>
                {posts === undefined &&
                    <div style={{ gridColumnStart: 1, gridColumnEnd: 3, marginBottom: 10, padding: "0 10px" }}>
                        <LoaderInline active infoText={t("loading data")} />
                    </div>
                }
                <T.SeqContainer height={maxLeftHeight} color={selectedNodeColor ?? "@middleLightGrey"} ref={leftContainerRef} id="left">
                    <ImplementedElementsHeadlineOnlyAAO style={{ paddingRight: 20 }} >
                        {t("implemented contents")}
                        <Button kind="halfTransparentButton" onClick={selectAllElements} icon="add" floated="right" tooltip={{ tooltipText: t("assign all elements to trainer content list"), background: "@accentGreen", fontColor: "#fff" }} />
                        <Button kind="halfTransparentButton" onClick={deSelectAllElements} icon="minus" floated="right" tooltip={{ tooltipText: t("remove all elements from trainer content list"), background: "@accentRed", fontColor: "#fff" }} />
                    </ImplementedElementsHeadlineOnlyAAO>
                    {_.map(groupedContents, (group, type) => {
                        const groupReferenceIds: string[] = group
                            .map(content => content.contents.referenceId)
                            .filter((id): id is string => !!id);
                        const isAnyElementSelected = s_trainerDocumentsReferenceIds.value?.some(id => groupReferenceIds.includes(id));
                        const allElementsSelected = groupReferenceIds.length > 0 && groupReferenceIds.every(id => s_trainerDocumentsReferenceIds.value?.includes(id));

                        const selectAllTypeElements = () => {
                            s_trainerDocumentsReferenceIds.setValue(_.uniq([...(s_trainerDocumentsReferenceIds.value ?? []), ...groupReferenceIds]));

                        };

                        const deSelectAllTypeElements = () => {
                            s_trainerDocumentsReferenceIds.setValue((s_trainerDocumentsReferenceIds.value ?? []).filter(id => !groupReferenceIds.includes(id)));
                        };

                        return (
                            <div key={type} style={{ paddingRight: 20 }}>
                                <ImplementedElementsHeadlineOnlyAAO style={{ marginBottom: -5, fontSize: "1em" }}>
                                    <div style={{ position: "relative" }}>
                                        {t(type)}
                                        {isAnyElementSelected &&
                                            <div style={{ position: "absolute", right: showExpView ? 72 : 39, zIndex: 50, top: 1 }}>
                                                <Button
                                                    size="small"
                                                    icon="minus"
                                                    onClick={deSelectAllTypeElements} kind="transparentButton"
                                                    tooltip={{ tooltipText: t("remove all elements of the {{group}} group from the trainer contents list", { group: t(type) }), background: "@accentRed", fontColor: "#fff", position: "bottom" }}
                                                />
                                            </div>
                                        }
                                        <div style={{ position: "absolute", right: showExpView ? 40 : 7, zIndex: 50, top: 1 }}>
                                            <Button
                                                disabled={allElementsSelected}
                                                size="small"
                                                icon="add"
                                                onClick={selectAllTypeElements}
                                                kind={allElementsSelected ? "cancelButton" : "transparentButton"}
                                                cursor={allElementsSelected ? "default" : "pointer"}
                                                tooltip={{ tooltipText: allElementsSelected ? t("all elements are already in trainer contents list") : t("assign all elements of the {{group}} group to the trainer contents list", { group: t(type) }), background: allElementsSelected ? "@darkGrey" : "@accentGreen", fontColor: "#fff", position: "top" }}
                                            />
                                        </div>
                                    </div>
                                </ImplementedElementsHeadlineOnlyAAO>
                                {_.map(_.sortBy(group, g => getPostHeadlineForContent(g as PlaylistContentDownloadDto).toUpperCase()), (p, i) => {
                                    if (p.contents.referenceId) {
                                        const pc = p.post;
                                        const contentIdx = _.findIndex(s_trainerDocumentsReferenceIds.value, (r) => r === p.contents.referenceId);
                                        const isIn = contentIdx >= 0;
                                        const numberOfUsed = _.find(s_trainerDocumentsReferenceIds.value, (r) => r === p.contents.referenceId) ? 1 : 0;
                                        const click = () => {
                                            const idx = _.findIndex(s_trainerDocumentsReferenceIds.value, (r) => r === p.contents.referenceId);
                                            if (idx >= 0)
                                                s_trainerDocumentsReferenceIds.setValue((s_trainerDocumentsReferenceIds.value ?? []).filter((_, i) => i !== idx));
                                            else
                                                s_trainerDocumentsReferenceIds.setValue([...(s_trainerDocumentsReferenceIds.value ?? []), p.contents.referenceId!]);
                                        }
                                        if (pc)
                                            return (
                                                <div key={i}>
                                                    <PlaylistEditSequenceImplementedElement
                                                        content={p.contents}
                                                        notUsed={!numberOfUsed}
                                                        post={pc}
                                                        key={i}
                                                        isLinear={false}
                                                        expandedView={showExpView}
                                                        select={click}
                                                        clickButtonProps={
                                                            {
                                                                onClick: click,
                                                                icon: isIn ? "checkbox checked" : "checkbox unchecked",
                                                                tooltip: { tooltipText: isIn ? t("remove from trainer contents list") : t("assign to trainer contents list"), background: isIn ? "@accentRed" : "@accentGreen", fontColor: "white" },
                                                            }
                                                        }
                                                        fullWidth
                                                        numberOfUsed={numberOfUsed}
                                                    />
                                                </div>
                                            );

                                        return <div key={i} />;
                                    }
                                    return null;
                                })
                                }
                            </div>
                        )
                    })}
                </T.SeqContainer>
                <div ref={rightContainerRef} id="right">
                    <T.ImplContainer height={maxRightHeight} color={selectedNodeColor ?? "@middleLightGrey"} style={{ border: "none", padding: 0, marginTop: 0, marginBottom: 0 }}>
                        <SequenceContainerOuter color={selectedNodeColor ?? "@middleLightGrey"}>
                            <ImplementedElementsHeadlineOnlyAAO >
                                {t("trainer contents list")}
                            </ImplementedElementsHeadlineOnlyAAO>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr max-content", rowGap: 2, columnGap: 5 }}>
                                {_.map(s_trainerDocumentsReferenceIds.value, (referenceId, i) => {
                                    const content = _.find(contents, c => c.contents.referenceId === referenceId);
                                    if (!content) return null;
                                    const pc = content.post;
                                    return (
                                        <React.Fragment key={i}>
                                            <PlaylistEditSequenceImplementedElement
                                                content={content.contents}
                                                notUsed={false}
                                                post={pc}
                                                key={i}
                                                fullWidth
                                                isLinear={false}
                                                expandedView={showExpView}
                                                numberOfUsed={0}
                                            />
                                            <Button
                                                onClick={() => s_trainerDocumentsReferenceIds.setValue((s_trainerDocumentsReferenceIds.value ?? []).filter(id => id !== referenceId))}
                                                icon="checkbox checked"
                                                floated="right"
                                                kind="transparentButton"
                                                tooltip={{ tooltipText: t("remove element from trainer contents list"), background: "@accentRed", fontColor: "#fff" }}
                                            />
                                        </React.Fragment>
                                    )
                                })}
                            </div>
                        </SequenceContainerOuter>

                    </T.ImplContainer>
                </div>
            </T.SeqContainerOuter>
        </div>
    )
}
export default PlaylistEditTrainerContent;